import type { AxiosRequestConfig } from 'axios'
import apiClient from '../client'
import type { PageResponse } from '../types'
import type {
  CalculationDispatchResponse,
  RecipientBankInfoCsvImportResponse,
  RecipientCreateRequest,
  RecipientResponse,
  RecipientUpdateRequest,
  DetailReviewPatchItem,
} from './types'

export const recipientsApi = {
  list(params?: {
    identId?: string
    importNo?: string
    householdIdentCode?: string
    name?: string
    noticeAddressee?: string
    address?: string
    welfareStatus?: string
    paymentStatus?: string
    paymentStatuses?: string
    dashboardPreset?: 'overallPending'
    /** 事務局確認用ステータス絞り込み。undefined=全て / 0=未設定(-) / 正の値=該当ステータスID */
    officeReviewStatusId?: number
    sortBy?: string
    order?: 'ASC' | 'DESC'
    page?: number
    size?: number
    excludeDeleted?: boolean
    /** 口座情報入力済みフィルタ。undefined=全て / true=入力済み / false=未入力 */
    bankInfoFilled?: boolean
    /** コメント有無フィルタ。undefined=全て / true=有（コメント非空） / false=−（空） */
    hasComment?: boolean
    /** 基本情報空欄有無フィルタ。undefined=全て / true=有（いずれか空欄） / false=−（全項目あり） */
    basicInfoBlank?: boolean
  }) {
    return apiClient.get<PageResponse<RecipientResponse>>('/api/recipients', { params })
  },

  getById(id: number) {
    return apiClient.get<RecipientResponse>(`/api/recipients/${id}`)
  },

  create(data: RecipientCreateRequest, config?: AxiosRequestConfig) {
    return apiClient.post<RecipientResponse>('/api/recipients', data, config)
  },

  update(id: number, data: RecipientUpdateRequest, config?: AxiosRequestConfig) {
    return apiClient.put<RecipientResponse>(`/api/recipients/${id}`, data, config)
  },

  /** 明細行の確認済み・行メモを更新（No.71。再計算・編集ロックなし） */
  putDetailReviews(id: number, items: DetailReviewPatchItem[], config?: AxiosRequestConfig) {
    return apiClient.put<RecipientResponse>(`/api/recipients/${id}/detail-reviews`, items, config)
  },

  delete(id: number) {
    return apiClient.delete<{ message: string }>(`/api/recipients/${id}`)
  },

  /** 案件ステータスを、より前の状態へ手動で戻す（No.78） */
  correctCaseStatus(id: number, body: { expectedStatus: number; targetStatus: number; reason: string }) {
    return apiClient.post<RecipientResponse>(`/api/recipients/${id}/case-status-corrections`, body)
  },

  /** 現金手渡済確定（NOTICE_DOWNLOADED の世帯を COMPLETED_CASH に遷移） */
  markCashHandover(id: number) {
    return apiClient.post<RecipientResponse>(`/api/recipients/${id}/cash-handover`)
  },

  /**
   * 事務局確認用ステータス（顧客要望 管理No.25）を設定する。案件ステータスとは非連動。
   * officeReviewStatusId に null を渡すと「未設定（-）」へクリアする。値は世帯単位で同期される。
   */
  setOfficeReviewStatus(id: number, officeReviewStatusId: number | null) {
    return apiClient.put<RecipientResponse>(`/api/recipients/${id}/office-review-status`, {
      officeReviewStatusId,
    })
  },

  dispatchCalculationRequests() {
    return apiClient.post<CalculationDispatchResponse>(
      '/api/recipients/calculation-requests/dispatch',
      undefined,
      { _silent: true },
    )
  },

  uploadBankInfoCsv(file: File, mappingJson: string) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('mappingJson', mappingJson)
    return apiClient.post<RecipientBankInfoCsvImportResponse>('/api/recipients/bank-info-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
    })
  },

  exportProgressExcel(params?: {
    identId?: string
    importNo?: string
    householdIdentCode?: string
    name?: string
    noticeAddressee?: string
    address?: string
    welfareStatus?: string
    paymentStatus?: string
    paymentStatuses?: string
    dashboardPreset?: 'overallPending'
    excludeDeleted?: boolean
    bankInfoFilled?: boolean
    officeReviewStatusId?: number
    hasComment?: boolean
    basicInfoBlank?: boolean
  }) {
    // エラー時は呼び出し側が blob 本文から取り出したメッセージを Modal で表示するため、
    // インターセプタの自動表示は抑止する（二重表示防止）。
    return apiClient.get<Blob>('/api/recipients/export', {
      params,
      responseType: 'blob',
      timeout: 600_000,
      _silent: true,
    })
  },

  exportIdentIdsCsv(params?: {
    identId?: string
    importNo?: string
    householdIdentCode?: string
    name?: string
    noticeAddressee?: string
    address?: string
    welfareStatus?: string
    paymentStatus?: string
    paymentStatuses?: string
    dashboardPreset?: string
    officeReviewStatusId?: number
    excludeDeleted?: boolean
    hasComment?: boolean
    basicInfoBlank?: boolean
  }) {
    // エラー時は呼び出し側が blob 本文から取り出したメッセージを Modal で表示するため、
    // インターセプタの自動表示は抑止する（二重表示防止）。
    return apiClient.get('/api/recipients/export-ident-ids', {
      params,
      responseType: 'blob',
      timeout: 600_000,
      _silent: true,
    })
  },

  exportRecipientsCsv(params?: {
    identId?: string
    importNo?: string
    householdIdentCode?: string
    name?: string
    noticeAddressee?: string
    address?: string
    welfareStatus?: string
    paymentStatus?: string
    paymentStatuses?: string
    dashboardPreset?: string
    officeReviewStatusId?: number
    excludeDeleted?: boolean
    hasComment?: boolean
    basicInfoBlank?: boolean
  }) {
    // エラー時は呼び出し側が blob 本文から取り出したメッセージを Modal で表示するため、
    // インターセプタの自動表示は抑止する（二重表示防止）。
    return apiClient.get('/api/recipients/export-recipients', {
      params,
      responseType: 'blob',
      timeout: 600_000,
      _silent: true,
    })
  },

  /**
   * 申出書情報出力（検討事項 No.69）。
   * 絞り込み条件に一致した世帯の申出書について、申出書詳細に登録された全項目を CSV 1 本で返す。
   * 出力対象は一覧画面の検索条件そのままなので、list() と同じ絞り込み項目を全て送る。
   */
  exportApplicationDocs(params?: {
    identId?: string
    importNo?: string
    householdIdentCode?: string
    name?: string
    noticeAddressee?: string
    address?: string
    welfareStatus?: string
    paymentStatus?: string
    paymentStatuses?: string
    dashboardPreset?: string
    officeReviewStatusId?: number
    excludeDeleted?: boolean
    hasComment?: boolean
    basicInfoBlank?: boolean
  }) {
    // エラー時は呼び出し側が blob 本文から取り出したメッセージを Modal で表示するため、
    // インターセプタの自動トーストは抑止する（二重表示防止）。
    return apiClient.get<Blob>('/api/recipients/export-application-docs', {
      params,
      responseType: 'blob',
      timeout: 600_000,
      _silent: true,
    })
  },

  /**
   * 国計算ツール出力（決定通知書作成済世帯の厚労省ツール xlsm を ZIP で一括DL）。
   * 出力対象は一覧画面の検索条件そのままなので、list() と同じ絞り込み項目を全て送る。
   */
  exportResultSheets(params?: {
    identId?: string
    importNo?: string
    householdIdentCode?: string
    name?: string
    noticeAddressee?: string
    address?: string
    welfareStatus?: string
    paymentStatus?: string
    paymentStatuses?: string
    dashboardPreset?: string
    officeReviewStatusId?: number
    excludeDeleted?: boolean
    hasComment?: boolean
    basicInfoBlank?: boolean
  }) {
    // エラー時は呼び出し側が blob 本文から取り出したメッセージを Modal で表示するため、
    // インターセプタの自動トーストは抑止する（二重表示防止）。
    return apiClient.get<Blob>('/api/recipients/export-result-sheets', {
      params,
      responseType: 'blob',
      timeout: 600_000,
      _silent: true,
    })
  },
}
